/* eslint-disable react/forbid-prop-types */
import { Component } from 'react'
import PropTypes from 'prop-types'
import { IndexRoute, Route, Router, Redirect } from 'react-router'
import { applyMiddleware, createStore } from 'redux'
import thunk from 'redux-thunk'
import { Provider } from 'react-redux'
import { map, values } from 'lodash'

import { withStaticProps } from 'lib/decorators'
import * as blUrls from 'lib/urls'
import { getHistory } from 'lib/history'
import * as BLConstants from 'shared/constants'

import PostTeaserList from 'hello_baby/components/post-teaser-list'
import TopicView from 'hello_baby/components/topic-view'
import { withContextualizedTracking, track } from 'lib/analytics'
import { getTopicSlug } from '../lib'
import helloBabyReducer, { getPostBySlug } from '../reducers'
import { SET_COLLECTIONS, SET_POSTS } from '../actions'
import App from '../components/app'
import LandingPage from './landing-page'
import PostWrapper from './post-wrapper'
import PostsDataLoader from './posts-data-loader'
import PostList from './post-list'
import {
  GEAR_SLUG,
  PARENTING_SLUG,
  PREGNANCY_SLUG,
  PREGNANCY_WEEK_BY_WEEK_SLUG,
  SAMPLE_REGISTRIES_SLUG,
  FRIENDS_AND_FAMILY_SLUG,
} from '../constants'
import AdminTool from '../components/admin-tool'

const middlewares = [thunk]

if (process.env.NODE_ENV === 'development') {
  // middlewares.push(createLogger())
}

const createStoreWithMiddleware = applyMiddleware(...middlewares)(createStore)

const scrollTo = (elId) => {
  const el = document.getElementById(elId)
  if (el) {
    document.body.scrollTop = el.offsetTop - 80 // 80px to account for sticky nav
  }
}

class HelloBabyApp extends Component {
  constructor(props) {
    super(props)

    this.state = { isAdmin: false }

    this.store = createStoreWithMiddleware(helloBabyReducer)
    this.store.dispatch({
      type: SET_COLLECTIONS,
      collections: props.collections,
    })
    this.store.dispatch({ type: SET_POSTS, posts: props.posts })
    this.history = getHistory({ path: props.path, useRouter: true })

    const { posts } = this.store.getState()

    const topicNavigation =
      props.collections[BLConstants.COLLECTION_SLUG_TOPIC_NAVIGATION]

    this.DecoratedLandingPage = withStaticProps('LandingPage', {
      isEmailSubscriber: props.isEmailSubscriber,
      topicNavigation,
      featureFlags: props.featureFlags,
      currentUser: props.currentUser,
    })(LandingPage)

    this.DecoratedApp = withStaticProps('App', {
      posts,
      topicNavigation,
      state: this.store.getState(),
    })(App)

    this.DecoratedPostList = withStaticProps('PostList', {
      topicNavigation,
      emailUrls: props.emailUrls,
    })(PostList)

    this.DecoratedPost = withStaticProps('PostWrapper', {
      emailUrls: props.emailUrls,
    })(PostWrapper)
  }

  getChildContext() {
    const { context } = this.props

    return context
  }

  componentDidMount() {
    const { path } = this.props

    this.updateGAMetrics(path.split('/').pop())
    this.scrollToArticleInPathVariable()
    if (window.currentUser?.admin) {
      this.setState({ isAdmin: true })
    }
  }

  static handleRouteChange = (prevState, nextState) => {
    if (nextState.location.pathname !== prevState.location.pathname) {
      const {
        params: { slug },
      } = nextState

      this.updateGAMetrics(slug, nextState.location.pathname)
      // if the mobile nav menu is open, hide it
      PubSub.publish(BLConstants.HIDE_MOBILE_NAV_MODAL_EVENT)
      // Scroll to the top of the page
      window.scrollTo(0, 0)
    }
  }

  scrollToArticleInPathVariable = () => {
    const { sectionId } = this.props

    if (sectionId) {
      scrollTo(sectionId)
    }
  }

  updateGAMetrics(slug, pathname) {
    const { ga } = window

    let type = null
    let publishedAtYYYYMM = null

    if (slug) {
      const post = getPostBySlug(slug, this.store.getState().posts)
      if (post && post.publishedAt) {
        publishedAtYYYYMM = post.publishedAt.slice(0, 7)
        type = getTopicSlug(post) || post.type
      }
    } else {
      type = pathname.split('/').pop()
    }

    // Content groups must be set before the pageview event is sent.
    // The values persist across router navigation unless explicity 'unset' by setting to null
    ga('set', 'contentGroup1', type) // GA content grouping 'Hello Baby Topics'
    ga('set', 'contentGroup2', publishedAtYYYYMM) // GA content grouping 'Hello Baby Publish Date'
    ga('set', 'contentGroup3', 'Hello Baby') // GA content grouping 'Content Group'
    ga('send', 'pageview', pathname) // send pageview event
  }

  render() {
    const { collections, emailUrls, searchPosts, topics } = this.props

    const searchResults = values(searchPosts)

    const { isAdmin } = this.state

    const postRedirects = [
      {
        from: `${blUrls.helloBabyPath}/pottery-barn-kids-baby-holiday-gifts`,
        to: `${blUrls.helloBabyPath}/best-gifts-baby-first-holiday`,
      },
      {
        from: `${blUrls.helloBabyPath}/nursery-design-tips`,
        to: `${blUrls.helloBabyPath}/nursery-organization-hacks`,
      },
    ]

    return (
      <Provider store={this.store}>
        <>
          {isAdmin && <AdminTool />}
          <Router history={this.history}>
            <Route
              component={this.DecoratedApp}
              path={blUrls.helloBabyPath}
              onChange={this.handleRouteChange}
            >
              <IndexRoute component={this.DecoratedLandingPage} />
              <Route
                component={(routerProps) => (
                  <PostsDataLoader shouldFetchPosts>
                    <PostList
                      {...routerProps}
                      description={`${searchResults.length} results`}
                      emailUrls={emailUrls}
                      title={`'${routerProps.location.query.search_term}'`}
                      topicNavigation={
                        collections[
                          BLConstants.COLLECTION_SLUG_TOPIC_NAVIGATION
                        ]
                      }
                    >
                      <PostTeaserList posts={searchResults} />
                    </PostList>
                  </PostsDataLoader>
                )}
                path={blUrls.helloBabyPostPath('search')}
              />
              <Route component={this.DecoratedPostList} path="/">
                <Route
                  component={() => <TopicView slug={GEAR_SLUG} />}
                  path={blUrls.helloBabyPostPath(GEAR_SLUG)}
                />
                <Route
                  component={() => (
                    <TopicView emailUrls={emailUrls} slug={PREGNANCY_SLUG} />
                  )}
                  params={{ topicSlug: PREGNANCY_SLUG }}
                  path={blUrls.helloBabyPostPath(PREGNANCY_SLUG)}
                />
                <Route
                  component={() => <TopicView slug={PARENTING_SLUG} />}
                  params={{ topicSlug: PARENTING_SLUG }}
                  path={blUrls.helloBabyPostPath(PARENTING_SLUG)}
                />
                <Route
                  component={() => <TopicView slug={SAMPLE_REGISTRIES_SLUG} />}
                  params={{ topicSlug: SAMPLE_REGISTRIES_SLUG }}
                  path={blUrls.helloBabyPostPath(SAMPLE_REGISTRIES_SLUG)}
                />
                <Route
                  component={() => (
                    <TopicView slug={PREGNANCY_WEEK_BY_WEEK_SLUG} />
                  )}
                  path={blUrls.helloBabyPostPath(PREGNANCY_WEEK_BY_WEEK_SLUG)}
                />
                <Route
                  component={() => <TopicView slug={FRIENDS_AND_FAMILY_SLUG} />}
                  params={{ topicSlug: FRIENDS_AND_FAMILY_SLUG }}
                  path={blUrls.helloBabyPostPath(FRIENDS_AND_FAMILY_SLUG)}
                />
                {map(topics, (topic) => (
                  <Route
                    component={() => <TopicView slug={topic.slug} />}
                    key={topic.slug}
                    path={blUrls.helloBabyPostPath(topic.slug)}
                  />
                ))}
              </Route>
              {postRedirects.map((redirect) => (
                <Redirect
                  from={redirect.from}
                  key={redirect.from}
                  to={redirect.to}
                />
              ))}
              <Route
                component={this.DecoratedPost}
                path={`${blUrls.helloBabyPath}/:slug`}
              />
              {/* used by iOS app for feed guides */}
              <Route
                component={this.DecoratedPost}
                path={`${blUrls.helloBabyPath}/embedded/:slug`}
              />
            </Route>
          </Router>
        </>
      </Provider>
    )
  }
}

HelloBabyApp.propTypes = {
  collections: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  emailUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
  ex3ExperimentGroupName: PropTypes.string.isRequired,
  featureFlags: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isEmailSubscriber: PropTypes.bool.isRequired,
  path: PropTypes.string.isRequired,
  posts: PropTypes.object.isRequired,
  searchPosts: PropTypes.array.isRequired,
  sectionId: PropTypes.string.isRequired,
  topics: PropTypes.object.isRequired,
}

HelloBabyApp.childContextTypes = {
  minimalView: PropTypes.bool,
}

// eslint-disable-next-line import/no-default-export
export default withContextualizedTracking({
  eventLocation: track.EventLocation.GUIDE,
})(HelloBabyApp)
